<template>
    <period-heatmap
        class="mt-4"
        :title="conceded ? 'Points Conceded by Period' : 'Team Scoring by Period'"
        :caption="caption"
        label-header="Match"
        total-header="PTS"
        :rows="rows"
        :palette="conceded ? 'green' : 'orange'"
        :peak-icon="conceded ? 'mdi-alert-circle' : 'mdi-fire'"
        :loading="loading"
        :thresholds="conceded ? concededThresholds : null"
        clickable
        @row-click="goToMatch"
    >
        <template v-slot:label="{ row }">
            <template v-if="row.isAverage">
                <v-icon :color="conceded ? '#26a662' : '#ff7133'">mdi-chart-bar</v-icon>
                <span class="heatmap-average-label secondary-font">Average</span>
                <span class="heatmap-match-meta">{{ row.matchCount }} {{ row.matchCount === 1 ? 'match' : 'matches' }}</span>
            </template>
            <template v-else>
                <span class="heatmap-match-date secondary-font">{{ formatDate(row.datetime) }}</span>
                <img
                    v-if="row.opponentLogo"
                    :src="`${assetsUrl}/${row.opponentLogo}`"
                    class="heatmap-opponent-logo"
                    @error="$event.target.style.visibility = 'hidden'"
                >
                <span class="heatmap-opponent-name">vs {{ row.opponentName }}</span>
                <span class="heatmap-match-meta">{{ row.cup ? 'Cup' : `R${row.round}` }}</span>
            </template>
        </template>
    </period-heatmap>
</template>

<script>
    import PeriodHeatmap from '@/components/PeriodHeatmap.vue'
    import matchService from '@/services/match'
    import { bucketPeriodScore } from '@/utils/periodStats'

    export default {
        name: 'TeamPeriodHeatmap',
        components: {
            PeriodHeatmap
        },
        props: {
            players: {
                type: Array,
                default: () => []
            },
            // false = points the team scored, true = points its opponents scored
            conceded: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                concededByMatch: {},
                // Points allowed in a period: up to 10 is great, 15-20 moderate, 25+ bad
                concededThresholds: [
                    { value: 10, color: [34, 160, 90], label: '≤10' },
                    { value: 15, color: [245, 200, 40], label: '15' },
                    { value: 20, color: [245, 160, 40], label: '20' },
                    { value: 25, color: [220, 53, 53], label: '25+' }
                ],
                loading: false
            }
        },
        computed: {
            caption() {
                return this.conceded
                    ? 'Opponent points in each period per match, with the season average on top · green ≤10, yellow 15–20, red 25+'
                    : 'Team points in each period per match, with the season average on top · shaded against each row\'s highest period'
            },
            // Match info plus the team's own period points, built from the players' per-game stats
            matches() {
                const byMatch = {}
                ;(this.players || []).forEach(player => {
                    (player.stats || []).forEach(game => {
                        if (!game.matchId) return
                        if (!byMatch[game.matchId]) {
                            byMatch[game.matchId] = {
                                id: game.matchId,
                                teamId: game.teamId,
                                datetime: game.matchDatetime,
                                round: game.round,
                                cup: game.cup ?? game.isCup,
                                opponentName: game.opponentTeamName,
                                opponentLogo: game.opponentTeamLogo,
                                scored: bucketPeriodScore({})
                            }
                        }
                        bucketPeriodScore(game.periodScore, byMatch[game.matchId].scored)
                    })
                })
                return Object.values(byMatch).sort((a, b) => new Date(b.datetime) - new Date(a.datetime))
            },
            matchIds() {
                return this.matches.map(match => match.id).join(',')
            },
            matchRows() {
                return this.matches
                    .map(match => ({ ...match, values: this.conceded ? this.concededByMatch[match.id] : match.scored }))
                    .filter(row => row.values && Object.values(row.values).some(points => points > 0))
            },
            averageRow() {
                const count = this.matchRows.length
                if (!count) return null
                const values = bucketPeriodScore({})
                this.matchRows.forEach(row => {
                    Object.keys(values).forEach(key => {
                        values[key] += row.values[key]
                    })
                })
                Object.keys(values).forEach(key => {
                    values[key] = values[key] / count
                })
                return { id: 'average', isAverage: true, matchCount: count, values, decimals: true, highlight: true }
            },
            rows() {
                return this.averageRow ? [this.averageRow, ...this.matchRows] : []
            }
        },
        watch: {
            matchIds: {
                handler() {
                    if (this.conceded) this.loadConceded()
                },
                immediate: true
            }
        },
        methods: {
            // The team's stats don't include opponents, so each match's box score is fetched once
            loadConceded() {
                const matches = this.matches
                const missing = matches.filter(match => !this.concededByMatch[match.id])
                if (!missing.length) return
                this.loading = true
                Promise.all(missing.map(match =>
                    matchService.getMatch(match.id)
                        .then(resp => [match.id, this.opponentPeriodPoints(resp.data, match)])
                        .catch(() => [match.id, null])
                )).then(results => {
                    const concededByMatch = { ...this.concededByMatch }
                    results.forEach(([id, values]) => {
                        if (values) concededByMatch[id] = values
                    })
                    this.concededByMatch = concededByMatch
                }).finally(() => {
                    this.loading = false
                })
            },
            opponentPeriodPoints(matchData, match) {
                if (!matchData || !matchData.team1 || !matchData.team2) return null
                const isTeam1 = Number(matchData.team1.id) === Number(match.teamId) ||
                    (matchData.team2.name === match.opponentName && Number(matchData.team2.id) !== Number(match.teamId))
                const opponent = isTeam1 ? matchData.team2 : matchData.team1
                const values = bucketPeriodScore({})
                ;(opponent.players || []).forEach(player => {
                    (player.stats || []).forEach(stat => bucketPeriodScore(stat.periodScore, values))
                })
                return values
            },
            formatDate(datetime) {
                if (!datetime) return ''
                return new Date(datetime).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })
            },
            goToMatch(row) {
                if (row.isAverage) return
                this.$router.push({ name: 'Match', params: { id: row.id } })
            }
        }
    }
</script>

<style scoped>
.heatmap-average-label {
    font-size: 1.05rem;
}

.heatmap-match-date {
    flex: 0 0 auto;
    font-size: 0.9rem;
    color: #8a8a8e;
}

.heatmap-opponent-logo {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    object-fit: contain;
}

.heatmap-opponent-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.05rem;
}

.heatmap-match-meta {
    flex: 0 0 auto;
    margin-left: auto;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    color: #8a8a8e;
    text-transform: uppercase;
}
</style>
